'use client';

import { useQuery } from '@tanstack/react-query';
import {
  getOrgLlmBillingSummary,
  type OrgLlmBillingSummary,
} from '@/apis/getOrgLlmBillingSummary';

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatTokens(n: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function UsageBar({
  label,
  usedLabel,
  capLabel,
  percentWithinCap,
}: {
  label: string;
  usedLabel: string;
  capLabel: string;
  percentWithinCap: number;
}) {
  const width = Math.max(0, Math.min(100, percentWithinCap));
  return (
    <div className="space-y-2">
      <div className="flex justify-between gap-3 text-ds-text-darker">
        <span className="ds-text-body-sm-medium text-ds-text-darker">
          {label}
        </span>
        <span className="ds-text-body-sm-medium text-ds-text-default tabular-nums">
          {usedLabel}
          <span className="text-ds-text-subtle"> / {capLabel}</span>
        </span>
      </div>
      <div className="w-full rounded-full bg-ds-gray-subtler h-2 overflow-hidden">
        <div
          className="h-2 rounded-full bg-ds-brand-default"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export function BillingLlmUsageSummary({
  organizationUUID,
  planTokensIncluded,
}: {
  organizationUUID: string;
  /** Token cap from the org's active Autumn plan (fallback when backend returns null). */
  planTokensIncluded?: number;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['orgLlmBillingSummary', organizationUUID],
    queryFn: () => getOrgLlmBillingSummary(organizationUUID),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="mb-8 rounded-lg border border-ds-border-subtlest bg-ds-surface-white overflow-hidden p-6">
        <p className="ds-text-body-sm-medium text-ds-text-subtle">
          Loading LLM usage…
        </p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mb-8 rounded-lg border border-ds-border-subtlest bg-ds-surface-white overflow-hidden p-6">
        <p className="ds-text-body-sm-medium text-ds-danger-default">
          Could not load LLM usage.
          {error instanceof Error ? ` ${error.message}` : ''}
        </p>
      </div>
    );
  }

  return (
    <BillingLlmUsageSummaryLoaded
      summary={data}
      planTokensIncluded={planTokensIncluded}
    />
  );
}

function BillingLlmUsageSummaryLoaded({
  summary,
  planTokensIncluded,
}: {
  summary: OrgLlmBillingSummary;
  planTokensIncluded?: number;
}) {
  const {
    total_cost_usd,
    total_tokens,
    active_cost_cap_usd,
    active_token_cap,
    remaining_included_cost_usd,
  } = summary;

  // null means Enterprise unlimited — no cap
  const isUnlimited = active_cost_cap_usd === null;

  // Use backend cap if present, otherwise fall back to the plan's token allowance
  const resolvedTokenCap = active_token_cap ?? planTokensIncluded ?? null;

  const costPct =
    active_cost_cap_usd !== null && active_cost_cap_usd > 0
      ? (total_cost_usd / active_cost_cap_usd) * 100
      : 0;

  const tokenPct =
    resolvedTokenCap && resolvedTokenCap > 0
      ? (total_tokens / resolvedTokenCap) * 100
      : 0;

  return (
    <div className="mb-8 rounded-lg border border-ds-border-subtlest bg-ds-surface-white overflow-hidden p-6">
      <div className="flex flex-col gap-1 mb-4">
        <h2 className="ds-text-title-sm-semibold text-ds-text-darker">
          LLM usage (organization)
        </h2>
        <p className="ds-text-body-sm-medium text-ds-text-subtle">
          Organization-wide metered AI usage; allowances depend on your plan.
        </p>
      </div>

      <div className="space-y-6">
        <UsageBar
          label="Included LLM cost"
          usedLabel={formatUsd(total_cost_usd)}
          capLabel={isUnlimited ? 'Unlimited' : formatUsd(active_cost_cap_usd!)}
          percentWithinCap={isUnlimited ? 0 : costPct}
        />
        <UsageBar
          label="Included tokens (input + output)"
          usedLabel={formatTokens(total_tokens)}
          capLabel={
            resolvedTokenCap !== null
              ? formatTokens(resolvedTokenCap)
              : isUnlimited
                ? 'Unlimited'
                : '—'
          }
          percentWithinCap={tokenPct}
        />
        <p className="ds-text-body-sm-medium text-ds-text-subtle">
          Estimated cost remaining in included allowance:{' '}
          <span className="ds-text-body-sm-medium text-ds-text-default tabular-nums">
            {remaining_included_cost_usd !== null
              ? formatUsd(remaining_included_cost_usd)
              : 'Unlimited'}
          </span>
        </p>
      </div>
    </div>
  );
}
